import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  
  Chip,
  Paper,
  Avatar,
  Tooltip,
 
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import DataTable from "react-data-table-component";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonIcon from "@mui/icons-material/Person";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A" },
    error: { main: "#DC2626" },
    success: { main: "#059669" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const StyledSearchInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px 11px 40px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const SearchWrapper = styled(Box)(() => ({
  position: "relative",
  "& .search-icon": {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: "#9CA3AF",
    pointerEvents: "none",
  },
}));

const StatBox = styled(Paper, {
  shouldForwardProp: (p) => p !== "accentcolor" && p !== "delay",
})(({ accentcolor, delay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(accentcolor || "#2563EB", 0.12)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  background: "#fff",
  flex: 1,
  minWidth: 140,
}));

const StatIconBox = styled(Box)(({ accentcolor }) => ({
  width: 42,
  height: 42,
  borderRadius: 11,
  flexShrink: 0,
  background: alpha(accentcolor || "#2563EB", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const EditBtn = styled(Button)(({ theme }) => ({
  minWidth: "unset",
  padding: "5px 12px",
  borderRadius: 7,
  fontSize: 11,
  fontWeight: 700,
  textTransform: "none",
  background: alpha("#2563EB", 0.08),
  color: "#2563EB",
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.18s ease",
  "&:hover": {
    background: "#2563EB",
    color: "#fff",
    transform: "translateY(-1px)",
    boxShadow: `0 4px 10px ${alpha("#2563EB", 0.3)}`,
  },
}));

const Spinner = styled(Box)(() => ({
  width: 34,
  height: 34,
  border: "3px solid rgba(37,99,235,0.15)",
  borderTop: "3px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

// ─── Custom DataTable styles ──────────────────────────────────────────────────
const tableCustomStyles = {
  table: {
    style: { fontFamily: "'Sora', sans-serif", background: "transparent" },
  },
  headRow: {
    style: {
      background: alpha("#F0F2F8", 0.8),
      borderRadius: "10px 10px 0 0",
      borderBottom: `1px solid ${alpha("#000", 0.07)}`,
      minHeight: 44,
    },
  },
  headCells: {
    style: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.7px",
      color: "#9CA3AF",
      fontFamily: "'Sora', sans-serif",
      paddingLeft: 16,
    },
  },
  rows: {
    style: {
      fontSize: 13,
      fontFamily: "'Sora', sans-serif",
      color: "#4B5563",
      minHeight: 58,
      borderBottom: `1px solid ${alpha("#000", 0.05)}`,
      transition: "background 0.15s ease",
      "&:last-child": { borderBottom: "none" },
    },
    highlightOnHoverStyle: {
      background: alpha("#2563EB", 0.03),
      borderRadius: 8,
      outline: "none",
      cursor: "default",
    },
  },
  cells: { style: { paddingLeft: 16 } },
  pagination: {
    style: {
      fontFamily: "'Sora', sans-serif",
      fontSize: 12,
      fontWeight: 600,
      color: "#4B5563",
      borderTop: `1px solid ${alpha("#000", 0.06)}`,
      padding: "12px 16px",
    },
  },
  noData: {
    style: {
      fontFamily: "'Sora', sans-serif",
      color: "#9CA3AF",
      padding: "40px 0",
    },
  },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminStudentPage() {
  const { countryId } = useParams();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchStudents = useCallback(() => {
    fetch(`${API_URL}/admin/students/${countryId}/`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        const sorted = data.sort((a, b) =>
          a.username.localeCompare(b.username)
        );
        setStudents(sorted);
        setFilteredStudents(sorted);
        setLoading(false);
      })
      .catch(() => {
        alert("Failed to fetch students.");
        setLoading(false);
      });
  }, [countryId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setFilteredStudents(
      !query
        ? students
        : students.filter((s) =>
            s.username.toLowerCase().includes(query.toLowerCase())
          )
    );
  };

  const editStudent = (id) => {
    if (id) navigate(`/admin-edit-student/${id}`);
    else alert("Invalid student ID");
  };

  // ── Stats derived ──
  const total = students.length;
  const staffCount = students.filter((s) => s.is_staff).length;
  const superCount = students.filter((s) => s.is_superuser).length;
  const regular = total - staffCount;

  // ── Table columns ──
  const columns = [
    {
      name: "Student",
      selector: (row) => row.username,
      sortable: true,
      cell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              flexShrink: 0,
              background: `linear-gradient(135deg, ${alpha(
                "#2563EB",
                0.15
              )}, ${alpha("#2563EB", 0.25)})`,
              color: "#2563EB",
              fontSize: 13,
              fontWeight: 800,
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.username?.[0]?.toUpperCase() || "?"}
          </Avatar>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F1117",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.username}
          </Typography>
        </Box>
      ),
      grow: 2,
    },
    {
      name: "Staff",
      selector: (row) => row.is_staff,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.is_staff ? "Staff" : "No"}
          size="small"
          sx={{
            fontFamily: "'Sora', sans-serif",
            fontWeight: 700,
            fontSize: 10,
            letterSpacing: "0.3px",
            background: row.is_staff
              ? alpha("#059669", 0.1)
              : alpha("#000", 0.05),
            color: row.is_staff ? "#059669" : "#9CA3AF",
            border: `1px solid ${
              row.is_staff ? alpha("#059669", 0.22) : alpha("#000", 0.08)
            }`,
          }}
        />
      ),
    },
    {
      name: "Superuser",
      selector: (row) => row.is_superuser,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.is_superuser ? "Super" : "No"}
          size="small"
          sx={{
            fontFamily: "'Sora', sans-serif",
            fontWeight: 700,
            fontSize: 10,
            letterSpacing: "0.3px",
            background: row.is_superuser
              ? alpha("#7C3AED", 0.1)
              : alpha("#000", 0.05),
            color: row.is_superuser ? "#7C3AED" : "#9CA3AF",
            border: `1px solid ${
              row.is_superuser ? alpha("#7C3AED", 0.22) : alpha("#000", 0.08)
            }`,
          }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Tooltip title="Edit student" arrow>
          <EditBtn onClick={() => editStudent(row.id)}>
            <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
            Edit
          </EditBtn>
        </Tooltip>
      ),
      right: true,
    },
  ];

  // ── Loading screen ──
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <link
          href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
          rel="stylesheet"
        />
        <PageWrapper>
          <ContentBox>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                gap: 2,
              }}
            >
              <Spinner />
              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.disabled",
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Loading students…
              </Typography>
            </Box>
          </ContentBox>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Students
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Country ID:{" "}
                <strong style={{ color: "#4B5563" }}>{countryId}</strong>
              </Typography>
            </Box>
            <Chip
              label={`${filteredStudents.length} shown`}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                background: alpha("#2563EB", 0.1),
                color: "#2563EB",
                border: `1px solid ${alpha("#2563EB", 0.2)}`,
              }}
            />
          </Box>

          {/* ── Stat boxes ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <StatBox accentcolor="#2563EB" delay={50}>
              <StatIconBox accentcolor="#2563EB">
                <PeopleAltIcon sx={{ fontSize: 20, color: "#2563EB" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {total}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Students
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#059669" delay={100}>
              <StatIconBox accentcolor="#059669">
                <AdminPanelSettingsIcon
                  sx={{ fontSize: 20, color: "#059669" }}
                />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {staffCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Staff Members
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#7C3AED" delay={150}>
              <StatIconBox accentcolor="#7C3AED">
                <VerifiedUserIcon sx={{ fontSize: 20, color: "#7C3AED" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {superCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Superusers
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#C4922A" delay={200}>
              <StatIconBox accentcolor="#C4922A">
                <PersonIcon sx={{ fontSize: 20, color: "#C4922A" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {regular}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Regular Users
                </Typography>
              </Box>
            </StatBox>
          </Box>

          {/* ── Table Card ── */}
          <SectionCard delay={240}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>All Students</CardLabel>

              {/* Search */}
              <SearchWrapper sx={{ minWidth: 260, maxWidth: 340, flex: 1 }}>
                <SearchIcon className="search-icon" />
                <StyledSearchInput
                  placeholder="Search by username…"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </SearchWrapper>
            </Box>

            {/* DataTable */}
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: `1px solid ${alpha("#000", 0.07)}`,
              }}
            >
              <DataTable
                columns={columns}
                data={filteredStudents}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                highlightOnHover
                responsive
                customStyles={tableCustomStyles}
                noDataComponent={
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 3,
                        background: alpha("#2563EB", 0.07),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      <PeopleAltIcon
                        sx={{
                          fontSize: 24,
                          color: "primary.main",
                          opacity: 0.4,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      {searchQuery
                        ? `No students match "${searchQuery}"`
                        : "No students found"}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
